import type { Session } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from './supabase';

export interface StaffProfile {
  id: string;
  full_name: string;
  email: string;
  role: 'admin' | 'staff';
}

interface AuthState {
  loading: boolean;
  session: Session | null;
  /** null = signed in but not on the staff list */
  profile: StaffProfile | null;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  loading: true, session: null, profile: null, signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<StaffProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!session) {
        setProfile(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      const { data } = await supabase
        .from('users')
        .select('id, full_name, email, role')
        .eq('id', session.user.id)
        .maybeSingle();
      if (!cancelled) {
        setProfile((data as StaffProfile) ?? null);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user.id]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return <AuthContext.Provider value={{ loading, session, profile, signOut }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
